import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Checkbox,
  CircularProgress,
  Alert,
  InputBase,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  Snackbar,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ReplayIcon from "@mui/icons-material/Replay";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  fetchClassById,
  fetchTestById,
  fetchStudentSummaries,
  fetchCourseRoster,
  grantRetake,
} from "../../api/backend";
import { useTheme, alpha } from "@mui/material/styles";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)",
  surface: "#FFFFFF", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-2)",
  pageBg: "#ffffff",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)",
  green: "#15803D", green50: "rgba(21,128,61,.10)", green100: "rgba(21,128,61,.18)",
  amber: "#B5651D", amber50: "rgba(181,101,29,.10)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)", danger100: "rgba(180,35,24,.18)",
  purple: "var(--color-accent-violet)", purple50: "rgba(107,79,187,.10)",
  shadow: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
};

const AVATAR_COLORS = [
  "var(--color-accent-violet)","var(--color-accent-violet)","#b5651d","var(--color-primary)",
  "#be185d","#15803d","var(--color-ink-2)","var(--color-warn)",
];

function avatarColor(id) {
  return AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function fmtTime(startedAt, submittedAt) {
  if (!startedAt || !submittedAt) return "—";
  const sec = Math.round((new Date(submittedAt) - new Date(startedAt)) / 1000);
  if (sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}m ${s}s`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export function deriveStatus(sub) {
  if (!sub.startedAt && !sub.submittedAt) return "NOT_STARTED";
  if (sub.startedAt && !sub.submittedAt) return "IN_PROGRESS";
  // Best attempt counts (same rule as sign-off): a pass stays a pass after a failed retake.
  if (sub.bestPassed === true || sub.passed === true) return "PASS";
  if (sub.passed === false) return "FAIL";
  return "FAIL";
}

function scorePct(sub) {
  if (sub.score == null || !sub.totalPoints) return null;
  return Math.round((sub.score / sub.totalPoints) * 100);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Breadcrumb({ parts }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "16px", flexWrap: "wrap" }}>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <ChevronRightIcon sx={{ fontSize: 14, color: T.ink4, opacity: 0.7 }} />
          )}
          {p.onClick ? (
            <Typography
              component="span"
              onClick={p.onClick}
              sx={{
                fontSize: 13, fontWeight: 600, color: tPrimary,
                cursor: "pointer", "&:hover": { textDecoration: "underline" },
              }}
            >
              {p.label}
            </Typography>
          ) : (
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: T.ink3 }}>
              {p.label}
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

function PassRateRing({ pct, color }) {
  const c = color || (pct >= 70 ? T.green : T.danger);
  return (
    <Box
      sx={{
        width: 86, height: 86, borderRadius: "50%", flexShrink: 0,
        background: `conic-gradient(${c} ${pct}%, ${T.line2} 0)`,
        display: "grid", placeItems: "center", position: "relative",
        "&::after": {
          content: '""', position: "absolute", inset: 8,
          background: "#fff", borderRadius: "50%",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1, color: T.ink, fontFamily: "var(--font-display)" }}>
          {pct}<Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: T.ink3 }}>%</Typography>
        </Typography>
        <Typography sx={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: T.ink3, mt: "3px" }}>
          pass rate
        </Typography>
      </Box>
    </Box>
  );
}

function Histogram({ distribution, passingBucket }) {
  const max = Math.max(...distribution, 1);
  const COLORS = [
    T.danger, T.danger, T.danger,
    T.amber, T.amber, T.amber, T.amber, T.amber,
    T.green, T.green,
  ];
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-end", height: 56, gap: "4px", mb: "8px" }}>
        {distribution.map((count, i) => {
          const h = count === 0 ? 4 : Math.max(8, Math.round((count / max) * 56));
          const isPassLine = i === passingBucket;
          return (
            <Box
              key={i}
              title={`${i * 10}–${i * 10 + 10}%: ${count} student${count !== 1 ? "s" : ""}`}
              sx={{
                flex: 1, height: h, borderRadius: "4px 4px 2px 2px",
                background: count === 0 ? T.line2 : COLORS[i],
                opacity: count === 0 ? 1 : 0.85,
                borderLeft: isPassLine ? `2px dashed ${T.ink4}` : "none",
                cursor: "default",
                "&:hover": { opacity: 1 },
                transition: "opacity .12s",
              }}
            />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", fontFamily: "monospace", fontSize: 10, color: T.ink3 }}>
        <span>0%</span><span>50%</span>
        <span style={{ color: T.ink2, fontWeight: 600 }}>{passingBucket * 10}% pass</span>
        <span>100%</span>
      </Box>
    </Box>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    PASS:        { bg: T.green50,   color: T.green,   label: "Passed" },
    FAIL:        { bg: T.danger50,  color: T.danger,  label: "Not Passed" },
    IN_PROGRESS: { bg: T.blue50,    color: T.blue,    label: "In Progress" },
    NOT_STARTED: { bg: T.surface3,  color: T.ink3,    label: "Not Started" },
    UNGRADED:    { bg: T.amber50,   color: T.amber,   label: "Needs grading" },
    LATE:        { bg: T.purple50,  color: T.purple,  label: "Late" },
  }[status] || { bg: T.surface3, color: T.ink3, label: status };

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center",
        height: 22, px: "10px", borderRadius: "999px",
        fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
        background: cfg.bg, color: cfg.color,
      }}
    >
      {cfg.label}
    </Box>
  );
}

function ScoreBar({ pct, status }) {
  const fillColor = status === "PASS" ? T.green : status === "IN_PROGRESS" ? T.blue : T.danger;
  if (pct == null) return <Typography sx={{ fontSize: 12.5, color: T.ink3, fontFamily: "monospace" }}>—</Typography>;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 180 }}>
      <Box sx={{ flex: 1, height: 6, background: T.line2, borderRadius: "999px", overflow: "hidden", maxWidth: 120 }}>
        <Box sx={{ height: "100%", width: `${pct}%`, background: fillColor, borderRadius: "999px", transition: "width .3s" }} />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {pct}<Typography component="span" sx={{ fontSize: 12, color: T.ink3, fontWeight: 500 }}>%</Typography>
      </Typography>
    </Box>
  );
}

function SortIcon({ col, sortBy, sortDir }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  if (sortBy !== col) return <Box component="span" sx={{ color: T.ink4, fontSize: 10, ml: "4px", opacity: 0.5 }}>⬍</Box>;
  return sortDir === "asc"
    ? <ArrowUpwardIcon sx={{ fontSize: 12, ml: "3px", color: tPrimary }} />
    : <ArrowDownwardIcon sx={{ fontSize: 12, ml: "3px", color: tPrimary }} />;
}

function ChipSet({ options, value, onChange }) {
  return (
    <Box sx={{
      display: "flex", gap: "3px",
      background: T.surface2, border: `1px solid ${T.line}`,
      borderRadius: "8px", p: "3px",
    }}>
      {options.map((opt) => (
        <Box
          key={opt.value}
          component="button"
          onClick={() => onChange(opt.value)}
          sx={{
            height: 26, px: "11px", borderRadius: "5px",
            font: "inherit", fontSize: 12, fontWeight: 600,
            color: value === opt.value ? T.ink : T.ink3,
            background: value === opt.value ? "#fff" : "transparent",
            border: value === opt.value ? `1px solid ${T.line}` : "1px solid transparent",
            boxShadow: value === opt.value ? "0 1px 2px rgba(20,24,40,.06)" : "none",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px",
            transition: "all .12s",
            "&:hover": { color: T.ink },
          }}
        >
          {opt.dot && (
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: opt.dot, flexShrink: 0 }} />
          )}
          {opt.label}
          {opt.count != null && (
            <Box component="span" sx={{
              fontFamily: "monospace", fontSize: "10.5px",
              background: value === opt.value ? T.surface3 : T.line2,
              color: value === opt.value ? T.ink2 : T.ink3,
              px: "4px", borderRadius: "3px", ml: "2px",
            }}>
              {opt.count}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
// Enrolled students with no submission have no summary row; add them as NOT_STARTED so they can be seen.
const withNotStarted = (mapped, tst, roster) => {
  const have = new Set(mapped.map((r) => r.studentId));
  const missing = (roster || [])
    .filter((u) => !have.has(String(u.id)))
    .map((u) => ({
      studentId: String(u.id), studentName: u.name || `Student ${u.id}`, studentHandle: String(u.id),
      status: "NOT_STARTED", score: null, pointsRaw: "—", startedAt: null, submittedAt: null,
      submissionCount: 0, effectiveMax: tst?.maxRetakes ?? 1, canRetake: false,
    }));
  return [...mapped, ...missing];
};

export default function TestAssessPage() {
  const { id: courseId, testId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const basePath = window.location.pathname.includes("/course-hub/")
    ? `/course-hub/${courseId}`
    : `/course-management/${courseId}`;

  const [course, setCourse] = React.useState(null);
  const [test, setTest] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Grant/menu state
  const [menuAnchor, setMenuAnchor] = React.useState(null); // {el, studentId, studentName, canRetake}
  const [grantLoading, setGrantLoading] = React.useState(false);
  const [snack, setSnack] = React.useState(null); // {msg, severity}

  // UI state
  const [filter, setFilter] = React.useState("all");
  const [query, setQuery] = React.useState("");
  const [sortBy, setSortBy] = React.useState("name");
  const [sortDir, setSortDir] = React.useState("asc");
  const [selected, setSelected] = React.useState(new Set());
  const [tab, setTab] = React.useState("students");

  const mapSummary = (s, tst) => ({
    studentId:       String(s.studentId),
    studentName:     s.studentName || `Student ${s.studentId}`,
    studentHandle:   s.studentHandle || String(s.studentId),
    status:          deriveStatus(s),
    score:           scorePct(s),
    pointsRaw:       s.score != null ? `${s.score}/${s.totalPoints ?? "?"}` : "—",
    startedAt:       s.startedAt,
    submittedAt:     s.submittedAt,
    submissionCount: s.submissionCount ?? 0,
    effectiveMax:    s.effectiveMax ?? tst?.maxRetakes ?? 1,
    canRetake:       s.canRetake === true,
  });

  const rosterRef = React.useRef(null);

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchClassById(courseId), fetchTestById(testId), fetchStudentSummaries(testId),
      fetchCourseRoster(courseId, { peersOnly: true }).catch(() => null),
    ])
      .then(([cls, tst, summaries, roster]) => {
        rosterRef.current = Array.isArray(roster) ? roster : null;
        setCourse(cls);
        setTest(tst);
        setRows(withNotStarted((summaries || []).map((s) => mapSummary(s, tst)), tst, rosterRef.current));
        setLoading(false);
      })
      .catch(() => { setError("Failed to load results."); setLoading(false); });
  }, [courseId, testId]);

  const reload = React.useCallback(() => {
    fetchStudentSummaries(testId)
      .then((summaries) => setRows(withNotStarted((summaries || []).map((s) => mapSummary(s, test)), test, rosterRef.current)))
      .catch(() => {});
  }, [testId, test]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const enrolledCount = rosterRef.current ? rosterRef.current.length : (course?.studentCount ?? rows.length);
  const passedRows = rows.filter((r) => r.status === "PASS");
  const failedRows = rows.filter((r) => r.status === "FAIL");
  const inProgRows = rows.filter((r) => r.status === "IN_PROGRESS");
  const notStartRows = rows.filter((r) => r.status === "NOT_STARTED");
  const notStartCount = notStartRows.length;
  const passRatePct = enrolledCount > 0
    ? Math.round((passedRows.length / enrolledCount) * 100) : 0;
  const completed = rows.filter((r) => r.submittedAt);
  const avgScorePct = completed.length > 0
    ? Math.round(completed.reduce((s, r) => s + (r.score ?? 0), 0) / completed.length) : 0;

  // Score distribution (10 buckets of 10%)
  const distribution = Array(10).fill(0);
  completed.forEach((r) => {
    if (r.score != null) {
      const bucket = Math.min(9, Math.floor(r.score / 10));
      distribution[bucket]++;
    }
  });
  const passingPct = test?.passingScore ?? 70;
  const passingBucket = Math.min(9, Math.floor(passingPct / 10));

  // ── Filtering & sorting ──────────────────────────────────────────────────
  const counts = {
    all: rows.length,
    PASS: passedRows.length,
    FAIL: failedRows.length,
    IN_PROGRESS: inProgRows.length,
    NOT_STARTED: notStartCount,
  };

  const q = query.toLowerCase();
  let visible = rows.filter((r) => {
    const matchFilter =
      filter === "all" ||
      r.status === filter;
    const matchQ =
      !q ||
      r.studentName.toLowerCase().includes(q) ||
      r.studentHandle.toLowerCase().includes(q);
    return matchFilter && matchQ;
  });

  // Sort
  visible = [...visible].sort((a, b) => {
    let va, vb;
    switch (sortBy) {
      case "status":   va = a.status;       vb = b.status;       break;
      case "score":    va = a.score ?? -1;  vb = b.score ?? -1;  break;
      case "submitted": va = a.submittedAt ?? ""; vb = b.submittedAt ?? ""; break;
      default:         va = a.studentName;  vb = b.studentName;  break;
    }
    const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  // ── Selection ────────────────────────────────────────────────────────────
  const visibleIds = visible.map((r) => r.studentId);
  const allChecked = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someChecked = visibleIds.some((id) => selected.has(id));

  const toggleAll = () => {
    if (allChecked) {
      setSelected((prev) => { const s = new Set(prev); visibleIds.forEach((id) => s.delete(id)); return s; });
    } else {
      setSelected((prev) => new Set([...prev, ...visibleIds]));
    }
  };
  const toggleRow = (id) => {
    setSelected((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const handleGrantRetake = async (studentId, studentName) => {
    setGrantLoading(true);
    try {
      await grantRetake(testId, studentId);
      setSnack({ msg: `Retake granted to ${studentName}.`, severity: "success" });
      reload();
    } catch (e) {
      setSnack({ msg: e?.response?.data?.error || "Failed to grant retake.", severity: "error" });
    } finally {
      setGrantLoading(false);
      setMenuAnchor(null);
      setSelected(new Set());
    }
  };

  const viewResults = (studentId) =>
    navigate(`${basePath}/tests/${testId}/results/${studentId}`);

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 700, mx: "auto", p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const FILTER_OPTS = [
    { value: "all",         label: "All",          count: counts.all },
    { value: "PASS",        label: "Passed",       count: counts.PASS,        dot: T.green },
    { value: "FAIL",        label: "Failed",       count: counts.FAIL,        dot: T.danger },
    { value: "IN_PROGRESS", label: "In progress",  count: counts.IN_PROGRESS, dot: T.blue },
    { value: "NOT_STARTED", label: "Not started",  count: counts.NOT_STARTED, dot: T.ink3 },
  ];

  const TH_SX = {
    fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase",
    color: T.ink3, background: "#FFFFFF", borderBottom: `1px solid ${T.line2}`,
    py: "12px", px: "16px", whiteSpace: "nowrap",
  };
  const TD_SX = { py: "14px", px: "16px", borderBottom: `1px solid ${T.line2}`, fontSize: "13.5px", color: T.ink };

  return (
    <Box sx={{ background: T.pageBg, minHeight: "100vh", p: { xs: "18px 18px 60px", md: "24px 40px 80px" } }}>
      <Box sx={{ maxWidth: 1320, mx: "auto" }}>

        {/* Breadcrumb */}
        <Breadcrumb parts={[
          { label: "Course Management", onClick: () => navigate(basePath) },
          { label: course?.title || `Course ${courseId}`, onClick: () => navigate(`${basePath}/tests`) },
          { label: "Tests", onClick: () => navigate(`${basePath}/tests`) },
          { label: `${test?.title || `Test ${testId}`} · Results` },
        ]} />

        {/* Page header */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: "18px", alignItems: "end", mb: "20px" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "4px" }}>
              <BarChartIcon sx={{ fontSize: 13, color: T.ink3 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: T.ink3 }}>
                Test results · {test?.classLevel ? "Class-level" : "Session"}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: "-0.02em", color: tPrimary, m: 0, fontFamily: "var(--font-display)" }}>
              {test?.title || `Test ${testId}`}
            </Typography>
            <Box sx={{ display: "flex", gap: "16px", mt: "6px", flexWrap: "wrap", fontFamily: "monospace", fontSize: 13, color: T.ink3 }}>
              <span><strong style={{ color: T.ink2 }}>{enrolledCount}</strong> enrolled</span>
              {passingPct > 0 && <span>Passing <strong style={{ color: T.ink2 }}>{passingPct}%</strong></span>}
              {test?.questionCount > 0 && <span><strong style={{ color: T.ink2 }}>{test.questionCount}</strong> questions</span>}
              {test?.timeLimitMinutes && <span>Limit <strong style={{ color: T.ink2 }}>{test.timeLimitMinutes} min</strong></span>}
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              sx={{ height: 30, fontSize: 12.5, fontWeight: 600, color: T.ink, background: "#fff", border: `1px solid ${T.line}`, borderRadius: "9px", px: "10px", "&:hover": { background: T.surface2, borderColor: "var(--color-hairline-cool)" } }}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<MailOutlineIcon sx={{ fontSize: 14 }} />}
              sx={{ height: 30, fontSize: 12.5, fontWeight: 600, background: tPrimary, borderRadius: "9px", px: "10px", "&:hover": { background: tPrimaryDk } }}
            >
              Message all students
            </Button>
          </Stack>
        </Box>

        {/* Overview strip */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 1fr" }, gap: "16px", mb: "20px" }}>

          {/* Pass-rate card */}
          <Box sx={{
            background: "#fff", borderRadius: "12px", border: `1px solid ${T.line2}`,
            boxShadow: T.shadow, p: "22px 24px",
            display: "grid", gridTemplateColumns: "auto 1fr", gap: "22px", alignItems: "center",
          }}>
            <PassRateRing pct={passRatePct} />
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink3, mb: "4px" }}>
                Class summary
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.005em", color: T.ink, mb: "6px", fontFamily: "var(--font-display)" }}>
                {passedRows.length} of {enrolledCount} students passed
              </Typography>
              <Typography sx={{ fontSize: 13, color: T.ink3 }}>
                Class average is{" "}
                <strong style={{ color: T.ink2 }}>{avgScorePct}%</strong>
                {passingPct > 0 && (
                  <>, {avgScorePct >= passingPct ? "above" : "below"} the{" "}
                  <strong style={{ color: T.ink2 }}>{passingPct}%</strong> passing mark</>
                )}.
              </Typography>
            </Box>
          </Box>

          {/* Score distribution */}
          <Box sx={{ background: "#fff", borderRadius: "12px", border: `1px solid ${T.line2}`, boxShadow: T.shadow, p: "18px 20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "10px" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: T.ink3, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink3 }}>
                Score distribution
              </Typography>
            </Box>
            {completed.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: T.ink3, py: "12px", textAlign: "center" }}>
                No submissions yet
              </Typography>
            ) : (
              <Histogram distribution={distribution} passingBucket={passingBucket} />
            )}
          </Box>

        </Box>

        {/* Main panel */}
        <Box sx={{ background: "#fff", borderRadius: "12px", border: `1px solid ${T.line2}`, boxShadow: T.shadow, overflow: "hidden" }}>

          {/* Sub-tabs */}
          <Box sx={{ display: "flex", px: "24px", borderBottom: `1px solid ${T.line2}`, gap: "4px" }}>
            {[
              { key: "students", label: "By Student", count: rows.length },
              { key: "analysis", label: "Item Analysis", count: `${test?.questionCount ?? 0}Q` },
              { key: "log", label: "Activity Log" },
            ].map((t) => (
              <Box
                key={t.key}
                component="button"
                onClick={() => setTab(t.key)}
                sx={{
                  py: "14px", px: "14px", font: "inherit", fontSize: 13.5, fontWeight: 600,
                  color: tab === t.key ? T.ink : T.ink3, background: "none", border: "none",
                  borderBottom: `2px solid ${tab === t.key ? tPrimary : "transparent"}`,
                  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px",
                  transition: "color .15s", "&:hover": { color: T.ink2 },
                }}
              >
                {t.label}
                {t.count != null && (
                  <Box component="span" sx={{
                    fontFamily: "monospace", fontSize: 11, fontWeight: 500,
                    background: tab === t.key ? alpha(tPrimary, 0.10) : T.surface3,
                    color: tab === t.key ? tPrimary : T.ink3,
                    px: "6px", py: "1px", borderRadius: "4px",
                  }}>
                    {t.count}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {tab !== "students" ? (
            <Box sx={{ p: "48px 24px", textAlign: "center" }}>
              <Typography sx={{ fontSize: 14, color: T.ink3 }}>
                {tab === "analysis" ? "Item analysis coming soon." : "Activity log coming soon."}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Toolbar */}
              <Box sx={{
                display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                gap: "12px", alignItems: "center", p: "14px 24px", borderBottom: `1px solid ${T.line2}`,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  {/* Search */}
                  <Box sx={{
                    position: "relative", flex: "0 1 260px",
                    background: T.surface2, border: `1px solid ${T.line}`, borderRadius: "8px",
                    display: "flex", alignItems: "center",
                    "&:focus-within": { background: "#fff", borderColor: tPrimary, boxShadow: `0 0 0 4px ${alpha(tPrimary, 0.08)}` },
                  }}>
                    <SearchIcon sx={{ fontSize: 15, color: T.ink3, ml: "10px", flexShrink: 0 }} />
                    <InputBase
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search students by name or ID…"
                      sx={{ flex: 1, px: "8px", py: "0px", fontSize: 13, height: 34, "& input": { py: 0 } }}
                    />
                  </Box>
                  <ChipSet options={FILTER_OPTS} value={filter} onChange={setFilter} />
                </Box>
                <Button
                  size="small"
                  startIcon={<FilterListIcon sx={{ fontSize: 13 }} />}
                  sx={{ height: 30, fontSize: 12.5, fontWeight: 600, color: T.ink, background: "#fff", border: `1px solid ${T.line}`, borderRadius: "7px", px: "10px", "&:hover": { background: T.surface2 } }}
                >
                  Filters
                </Button>
              </Box>

              {/* Bulk actions bar */}
              {selected.size > 0 && (
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: "24px", py: "10px", background: alpha(tPrimary, 0.10), borderBottom: `1px solid ${T.line2}`,
                  fontSize: 13, color: tPrimary, fontWeight: 600,
                }}>
                  <span><strong>{selected.size}</strong> student{selected.size !== 1 ? "s" : ""} selected</span>
                  <Stack direction="row" spacing={0.75}>
                    <Button size="small" sx={{ height: 28, fontSize: 12, fontWeight: 600, color: tPrimary, borderRadius: "7px", px: "10px", "&:hover": { background: alpha(tPrimary, 0.08) } }}>
                      Message
                    </Button>
                    <Button
                      size="small"
                      disabled={grantLoading}
                      onClick={async () => {
                        setGrantLoading(true);
                        try {
                          for (const sid of selected) {
                            await grantRetake(testId, sid);
                          }
                          setSnack({ msg: `Retake granted to ${selected.size} student${selected.size !== 1 ? "s" : ""}.`, severity: "success" });
                          reload();
                        } catch (e) {
                          setSnack({ msg: e?.response?.data?.error || "Failed to grant retakes.", severity: "error" });
                        } finally {
                          setGrantLoading(false);
                          setSelected(new Set());
                        }
                      }}
                      sx={{ height: 28, fontSize: 12, fontWeight: 600, color: tPrimary, borderRadius: "7px", px: "10px", "&:hover": { background: alpha(tPrimary, 0.08) } }}
                    >
                      {grantLoading ? "Granting…" : "Grant retake"}
                    </Button>
                    <Button size="small" sx={{ height: 28, fontSize: 12, fontWeight: 600, color: tPrimary, borderRadius: "7px", px: "10px", "&:hover": { background: alpha(tPrimary, 0.08) } }}>
                      Export selection
                    </Button>
                    <Button size="small" onClick={() => setSelected(new Set())} sx={{ height: 28, fontSize: 12, color: T.ink3, borderRadius: "7px", px: "8px" }}>
                      Clear
                    </Button>
                  </Stack>
                </Box>
              )}

              {/* Table */}
              <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...TH_SX, width: 40 }}>
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={someChecked && !allChecked}
                        onChange={toggleAll}
                        sx={{ p: 0, color: T.ink4, "&.Mui-checked": { color: tPrimary }, "&.MuiCheckbox-indeterminate": { color: tPrimary } }}
                      />
                    </TableCell>
                    {[
                      { key: "name",      label: "Student" },
                      { key: "status",    label: "Status" },
                      { key: "score",     label: "Score" },
                      { key: "submitted", label: "Submitted", hide: "sm" },
                    ].map((col) => (
                      <TableCell
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        sx={{ ...TH_SX, cursor: "pointer", "&:hover": { color: T.ink }, display: { xs: col.hide === "sm" ? "none" : "table-cell", md: "table-cell" } }}
                      >
                        {col.label}
                        <SortIcon col={col.key} sortBy={sortBy} sortDir={sortDir} />
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...TH_SX, textAlign: "right", width: 160 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ ...TD_SX, textAlign: "center", py: "40px" }}>
                        <Typography sx={{ fontSize: 13, color: T.ink3 }}>
                          {query || filter !== "all"
                            ? "No students match this filter — try clearing the search or status filters."
                            : "No students have taken this test yet."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {visible.map((row) => (
                    <TableRow
                      key={row.studentId}
                      onClick={() => { if (row.submittedAt) viewResults(row.studentId); }}
                      sx={{
                        cursor: row.submittedAt ? "pointer" : "default",
                        background: selected.has(row.studentId) ? alpha(tPrimary, 0.10) : "transparent",
                        "&:hover": { background: selected.has(row.studentId) ? alpha(tPrimary, 0.10) : T.surface2 },
                        "&:last-child td": { borderBottom: 0 },
                        transition: "background .12s",
                      }}
                    >
                      {/* Checkbox */}
                      <TableCell sx={{ ...TD_SX, width: 40 }} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selected.has(row.studentId)}
                          onChange={() => toggleRow(row.studentId)}
                          sx={{ p: 0, color: T.ink4, "&.Mui-checked": { color: tPrimary } }}
                        />
                      </TableCell>

                      {/* Student */}
                      <TableCell sx={{ ...TD_SX, minWidth: 200 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: "50%",
                            background: avatarColor(row.studentId),
                            display: "grid", placeItems: "center",
                            color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0,
                          }}>
                            {initials(row.studentName)}
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: T.ink }}>
                              {row.studentName}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: T.ink3, fontFamily: "monospace" }}>
                              {row.studentHandle}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={TD_SX}>
                        <StatusBadge status={row.status} />
                      </TableCell>

                      {/* Score */}
                      <TableCell sx={TD_SX}>
                        <Box>
                          <ScoreBar pct={row.score} status={row.status} />
                          {row.score != null && (
                            <Typography sx={{ fontSize: 11.5, color: T.ink3, fontFamily: "monospace", mt: "2px" }}>
                              {row.pointsRaw}
                            </Typography>
                          )}
                          {row.submissionCount > 0 && (
                            <Box sx={{ fontSize: 11, color: T.ink3, mt: "2px", fontFamily: "'JetBrains Mono', monospace" }}>
                              {row.submissionCount}/{row.effectiveMax} attempt{row.effectiveMax !== 1 ? "s" : ""}
                              {!row.canRetake && row.status === "FAIL" && (
                                <Box component="span" sx={{ ml: "4px", color: T.danger, fontWeight: 600 }}>· limit reached</Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Submitted */}
                      <TableCell sx={{ ...TD_SX, display: { xs: "none", md: "table-cell" } }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 12.5, color: T.ink2 }}>
                          {row.status === "IN_PROGRESS" ? "in progress" : fmtDate(row.submittedAt)}
                        </Typography>
                        {row.startedAt && row.submittedAt && (
                          <Typography sx={{ fontFamily: "monospace", fontSize: 10, color: T.ink4, mt: "2px", textTransform: "uppercase", letterSpacing: ".04em" }}>
                            {fmtTime(row.startedAt, row.submittedAt)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ ...TD_SX, textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                        {row.status === "NOT_STARTED" ? null : row.status === "IN_PROGRESS" ? (
                          <Box component="button" sx={{ width: 30, height: 30, borderRadius: "7px", border: "none", background: "transparent", color: T.ink3, display: "inline-grid", placeItems: "center", cursor: "pointer", "&:hover": { background: T.surface3, color: T.ink } }}>
                            <VisibilityIcon sx={{ fontSize: 15 }} />
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => viewResults(row.studentId)}
                            sx={{ height: 28, fontSize: 12, fontWeight: 600, color: T.ink2, borderRadius: "7px", px: "10px", "&:hover": { background: T.surface3, color: T.ink } }}
                          >
                            View answers
                          </Button>
                        )}
                        <Box
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor({ el: e.currentTarget, studentId: row.studentId, studentName: row.studentName, canRetake: row.canRetake });
                          }}
                          sx={{ width: 30, height: 30, borderRadius: "7px", border: "none", background: "transparent", color: T.ink3, display: "inline-grid", placeItems: "center", cursor: "pointer", ml: "4px", "&:hover": { background: T.surface3, color: T.ink } }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 15 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Panel footer */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "24px", py: "12px", borderTop: `1px solid ${T.line2}`, fontSize: 12.5, color: T.ink3 }}>
                <span>Showing <strong style={{ color: T.ink }}>{visible.length}</strong> of <strong style={{ color: T.ink }}>{rows.length}</strong> students</span>
              </Box>

              {/* Per-row context menu */}
              <Menu
                anchorEl={menuAnchor?.el}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                PaperProps={{ sx: { minWidth: 180, borderRadius: "10px", boxShadow: "0 4px 20px rgba(20,24,40,.12)", border: `1px solid ${T.line}` } }}
              >
                <MenuItem
                  disabled={grantLoading}
                  onClick={() => handleGrantRetake(menuAnchor.studentId, menuAnchor.studentName)}
                  sx={{ fontSize: 13, gap: "8px", color: T.ink }}
                >
                  <ReplayIcon sx={{ fontSize: 15, color: T.ink3 }} />
                  Grant retake
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Feedback snackbar */}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={snack?.msg}
      />
    </Box>
  );
}
